import { ChevronLeft, ChevronRight } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** Viewer menus render above the viewer's z-[60] chrome. */
const VIEWER_MENU_Z_CLASS = "z-[160]";

export interface ViewerSourceOption {
  sourceId: string;
  displayName: string;
}

export interface ViewerSourceSelectProps {
  sources: ReadonlyArray<ViewerSourceOption>;
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible name, e.g. "Drawing". */
  label?: string;
  placeholder?: string;
}

/** Drawing/document picker for the `source` slot of `ViewerTopBar`. */
export function ViewerSourceSelect({
  sources,
  value,
  onValueChange,
  label = "Document",
  placeholder = "Select document",
}: ViewerSourceSelectProps) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-8 w-40 text-sm sm:w-56" aria-label={label}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={VIEWER_MENU_Z_CLASS}>
        {sources.map((source) => (
          <SelectItem key={source.sourceId} value={source.sourceId}>
            {source.displayName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface ViewerPagerProps {
  activePage: number;
  pageCount: number;
  onGoToPage: (page: number) => void;
  /** Sheet labels shown with page numbers. */
  pageLabels?: ReadonlyMap<number, string>;
}

/** Previous / page select / next. Renders nothing for single-page sources. */
export function ViewerPager({ activePage, pageCount, onGoToPage, pageLabels }: ViewerPagerProps) {
  if (pageCount <= 1) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      <IconButton
        label="Previous page"
        icon={ChevronLeft}
        tooltipSide="bottom"
        disabled={activePage <= 1}
        onClick={() => onGoToPage(activePage - 1)}
      />
      <Select value={String(activePage)} onValueChange={(value) => onGoToPage(Number(value))}>
        <SelectTrigger
          className={cn("h-8 text-xs font-medium tabular-nums", pageLabels?.size ? "w-48" : "w-24")}
          aria-label="Go to page"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className={VIEWER_MENU_Z_CLASS}>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((page) => (
            <SelectItem key={page} value={String(page)} className="text-xs">
              Pg {page} / {pageCount}
              {pageLabels?.has(page) ? ` · ${pageLabels.get(page)}` : null}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <IconButton
        label="Next page"
        icon={ChevronRight}
        tooltipSide="bottom"
        disabled={activePage >= pageCount}
        onClick={() => onGoToPage(activePage + 1)}
      />
    </div>
  );
}
